import React, { useState, useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Skeleton } from '@/components/ui/skeleton';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import { AlertCircle, Users, MapPin, FileImage, ChevronLeft, ChevronRight } from 'lucide-react';
import { apiJson } from '@/lib/api';
import { dbg } from '@/lib/dbg';

interface AssignmentEntry {
  operatorId: number | null;
  operatorName: string;
  storeId: number;
  storeName: string;
  mediaFormatId: number | null;
  mediaFormatName: string;
  quantity: number;
  coveragePercent?: number | null;  // Only present for POPAd (limited capacity) formats
  packId?: number;
  packName?: string;
}

interface AssignmentsResponse {
  data: AssignmentEntry[];
  totalCount: number;
  totalStores: number;
  totalOperators: number;
  page: number;
  pageSize: number;
}

interface GroupedAssignments {
  [operatorName: string]: {
    operatorId: number | null;
    stores: {
      [storeName: string]: {
        storeId: number;
        formats: Array<{
          mediaFormatId: number | null;
          mediaFormatName: string;
          quantity: number;
          coveragePercent?: number | null;
        }>;
      };
    };
  };
}

interface AssignmentsTabProps { campaignId: number; }

export function AssignmentsTab({ campaignId }: AssignmentsTabProps) {
  const [page, setPage] = useState(1);
  const pageSize = 100; // Show more items per page for assignments

  // OPTIMIZED: Use new slim /api/campaigns/:id/assignments endpoint
  // This replaces the heavy /api/campaign-snapshots/:id/details call
  const {
    data: assignmentsData,
    isLoading,
    error,
    isError
  } = useQuery<AssignmentsResponse>({
    queryKey: ['/api/campaigns', campaignId, 'assignments', page, pageSize],
    queryFn: () => apiJson(`/api/campaigns/${campaignId}/assignments?page=${page}&pageSize=${pageSize}`),
    enabled: !!campaignId,
  });

  // Dev logging for debugging
  React.useEffect(() => {
    if (assignmentsData) {
      dbg.log('[ASSIGNMENTS_TAB] Using optimized endpoint', {
        dataLength: assignmentsData.data?.length || 0,
        totalCount: assignmentsData.totalCount,
        page: assignmentsData.page,
      });
    }
  }, [assignmentsData]);

  // OPTIMIZED: Assignments come pre-formatted from slim endpoint
  const assignments = assignmentsData?.data || [];
  const totalCount = assignmentsData?.totalCount || 0;
  const totalPages = Math.ceil(totalCount / pageSize);

  // Group assignments by operator → store → formats
  // IMPORTANT: Must be called before any early returns (React hooks rule)
  const groupedAssignments = useMemo(() => {
    return assignments.reduce((acc: GroupedAssignments, assignment: AssignmentEntry) => {
      const operatorName = assignment.operatorName || 'Unassigned';
      const storeName = assignment.storeName || `Store ${assignment.storeId}`;
      
      if (!acc[operatorName]) {
        acc[operatorName] = {
          operatorId: assignment.operatorId,
          stores: {}
        };
      }
      
      if (!acc[operatorName].stores[storeName]) {
        acc[operatorName].stores[storeName] = {
          storeId: assignment.storeId,
          formats: []
        };
      }
      
      // Check if this exact format already exists for this store-operator combination
      const existingFormat = acc[operatorName].stores[storeName].formats.find(
        (f) => f.mediaFormatId === assignment.mediaFormatId
      );
      
      if (!existingFormat) {
        acc[operatorName].stores[storeName].formats.push({
          mediaFormatId: assignment.mediaFormatId,
          mediaFormatName: assignment.mediaFormatName || 'Unknown Format',
          quantity: assignment.quantity || 1,
          coveragePercent: assignment.coveragePercent,
        });
      }
      
      return acc;
    }, {} as GroupedAssignments);
  }, [assignments]);

  if (isLoading) {
    return (
      <div className="space-y-4">
        <div className="flex items-center gap-2">
          <Users className="h-5 w-5" />
          <h2 className="text-xl font-semibold">Campaign Assignments</h2>
        </div>
        <div className="space-y-4">
          {[1, 2, 3].map((i) => (
            <Card key={i}>
              <CardHeader>
                <Skeleton className="h-6 w-48" />
              </CardHeader>
              <CardContent>
                <Skeleton className="h-32 w-full" />
              </CardContent>
            </Card>
          ))}
        </div>
      </div>
    );
  }

  if (isError) {
    dbg.error('AssignmentsTab: Error loading assignments', error);
    return (
      <div className="space-y-4">
        <div className="flex items-center gap-2">
          <Users className="h-5 w-5" />
          <h2 className="text-xl font-semibold">Campaign Assignments</h2>
        </div>
        <Alert variant="destructive">
          <AlertCircle className="h-4 w-4" />
          <AlertDescription>
            Failed to load campaign assignments. {error instanceof Error ? error.message : 'Unknown error occurred.'}
          </AlertDescription>
        </Alert>
      </div>
    );
  }

  if (assignments.length === 0 && totalCount === 0) {
    return (
      <div className="space-y-4">
        <div className="flex items-center gap-2">
          <Users className="h-5 w-5" />
          <h2 className="text-xl font-semibold">Campaign Assignments</h2>
        </div>
        <Alert>
          <AlertCircle className="h-4 w-4" />
          <AlertDescription>
            No assignments available for this campaign.
          </AlertDescription>
        </Alert>
      </div>
    );
  }

  const operatorCount = assignmentsData?.totalOperators ?? Object.keys(groupedAssignments).length;
  const storeCount = assignmentsData?.totalStores ?? Object.values(groupedAssignments).reduce((total, operator) => 
    total + Object.keys(operator.stores).length, 0
  );
  const displayedAssignments = assignments.length;

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Users className="h-5 w-5" />
          <h2 className="text-xl font-semibold">Campaign Assignments</h2>
        </div>
        <div className="flex items-center gap-4 text-sm text-muted-foreground">
          <div className="flex items-center gap-1">
            <Users className="h-4 w-4" />
            <span>{operatorCount} operators</span>
          </div>
          <div className="flex items-center gap-1">
            <MapPin className="h-4 w-4" />
            <span>{storeCount} stores</span>
          </div>
          <div className="flex items-center gap-1">
            <FileImage className="h-4 w-4" />
            <span>{displayedAssignments} of {totalCount} assignments</span>
          </div>
        </div>
      </div>

      {/* Pagination controls */}
      {totalPages > 1 && (
        <div className="flex items-center justify-between border-t pt-4">
          <div className="text-sm text-muted-foreground">
            Page {page} of {totalPages}
          </div>
          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={() => setPage(p => Math.max(1, p - 1))}
              disabled={page === 1}
            >
              <ChevronLeft className="h-4 w-4" />
              Previous
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={() => setPage(p => Math.min(totalPages, p + 1))}
              disabled={page === totalPages}
            >
              Next
              <ChevronRight className="h-4 w-4" />
            </Button>
          </div>
        </div>
      )}

      <div className="space-y-6">
        {Object.entries(groupedAssignments).map(([operatorName, operatorData]) => (
          <Card key={operatorName} className="border-l-4 border-l-blue-500">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Users className="h-5 w-5 text-blue-600" />
                {operatorName}
                <span className="text-sm font-normal text-muted-foreground">
                  ({Object.keys(operatorData.stores).length} stores)
                </span>
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              {Object.entries(operatorData.stores).map(([storeName, storeData]) => (
                <div key={`${operatorName}-${storeName}`} className="border rounded-lg p-4 bg-gray-50/50">
                  <div className="flex items-center gap-2 mb-3">
                    <MapPin className="h-4 w-4 text-gray-600" />
                    <h4 className="font-medium">{storeName}</h4>
                    <span className="text-sm text-muted-foreground">
                      ({storeData.formats.length} formats)
                    </span>
                  </div>
                  
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Format</TableHead>
                        <TableHead className="text-right">Quantity</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {storeData.formats.map((format, index) => (
                        <TableRow key={`${format.mediaFormatId}-${index}`}>
                          <TableCell className="font-medium">
                            {format.mediaFormatName}
                          </TableCell>
                          <TableCell className="text-right">
                            {format.quantity}
                            {format.coveragePercent != null && (
                              <span className="text-muted-foreground ml-1">({format.coveragePercent}%)</span>
                            )}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              ))}
            </CardContent>
          </Card>
        ))}
      </div>
    </div>
  );
}
